import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TurnstileHandle } from 'src/services/turnstileService';

const POLL_INTERVAL_MS = 200;
/** Report the missing Cloudflare script so forms stop waiting — but keep polling for a late one. */
const SCRIPT_READY_TIMEOUT_MS = 10000;
/** The only failure worth giving up on: Cloudflare's own 300*** challenge errors are retryable. */
export const SCRIPT_UNAVAILABLE = 'script-unavailable';
/** Cloudflare waits 8s by default; a slow link needs several attempts, so don't idle between them. */
const RETRY_INTERVAL_MS = 3000;

type Props = {
  siteKey: string;
  onTokenChange?: (token: string | null) => void;
  onError?: (errorCode: string) => void;
};

const Turnstile = forwardRef<TurnstileHandle, Props>(({ siteKey, onTokenChange, onError }, ref) => {
  const { i18n } = useTranslation();
  const language = i18n.language?.toLowerCase() || 'auto';

  // Frozen at mount: i18n resolves the language shortly after, and re-rendering the widget for that
  // would throw away a valid token and restart the challenge. An invisible widget shows no text anyway.
  const languageRef = useRef(language);
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenChangeRef = useRef(onTokenChange);
  const onErrorRef = useRef(onError);
  const [isApiReady, setIsApiReady] = useState<boolean>(Boolean(window.turnstile));

  useEffect(() => {
    onTokenChangeRef.current = onTokenChange;
    onErrorRef.current = onError;
  }, [onTokenChange, onError]);

  // Not turnstile.ready(): it throws while api.js is async/defer, so its presence is the signal.
  useEffect(() => {
    if (window.turnstile) {
      setIsApiReady(true);

      return;
    }

    const startedAt = Date.now();
    let hasReportedTimeout = false;

    const intervalId = window.setInterval(() => {
      if (window.turnstile) {
        setIsApiReady(true);
        window.clearInterval(intervalId);

        return;
      }

      // Report once so forms stop waiting, but keep polling: a late script still protects requests.
      if (!hasReportedTimeout && Date.now() - startedAt >= SCRIPT_READY_TIMEOUT_MS) {
        hasReportedTimeout = true;
        onErrorRef.current?.(SCRIPT_UNAVAILABLE);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    /** In 'render' mode reset() re-runs the challenge on its own, producing the next token. */
    refresh: () => {
      if (!window.turnstile || !widgetIdRef.current) {
        return;
      }

      window.turnstile.reset(widgetIdRef.current);
    },
  }));

  useEffect(() => {
    if (!isApiReady || !window.turnstile || !widgetContainerRef.current || widgetIdRef.current) {
      return;
    }

    widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
      sitekey: siteKey,
      language: languageRef.current,
      // The challenge starts as soon as this renders, so a token is ready long before it is needed.
      execution: 'render',
      appearance: 'interaction-only',
      retry: 'auto',
      'retry-interval': RETRY_INTERVAL_MS,
      callback: (token) => onTokenChangeRef.current?.(token),
      'expired-callback': () => onTokenChangeRef.current?.(null),
      // Never reset here: that cancels Cloudflare's own retry (retry defaults to 'auto').
      'error-callback': (errorCode: string) => {
        onTokenChangeRef.current?.(null);
        onErrorRef.current?.(errorCode);
      },
    });

    return () => {
      if (!window.turnstile || !widgetIdRef.current) {
        return;
      }

      if (window.turnstile.remove) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      onTokenChangeRef.current?.(null);
    };
  }, [isApiReady, siteKey]);

  return <div ref={widgetContainerRef} />;
});

Turnstile.displayName = 'Turnstile';

export default Turnstile;
